import React, { useEffect, useState } from 'react';
import RegistrationForm from './RegistrationForm';
import { saveUser } from '../utils/userStorage';
import { PEEKPASS_BRAND } from '../utils/peekPassBrand';
import { getRecaptchaToken } from '../services/recaptcha';

type RegistrationStep = 'basic' | 'complete';

interface BasicFormData {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;
  country: string;
  timezone: string;
}

interface RegistrationFlowProps {
  onGoToLogin?: () => void;
  onRegistrationComplete?: (userData: any) => void;
}

const RegistrationFlow: React.FC<RegistrationFlowProps> = ({ onGoToLogin, onRegistrationComplete }) => {
  const [currentStep, setCurrentStep] = useState<RegistrationStep>('basic');
  const [basicData, setBasicData] = useState<BasicFormData | null>(null);
  const [savedUserData, setSavedUserData] = useState<any | null>(null);

  const handleBasicFormComplete = async (data: BasicFormData) => {
    try {
      console.log('🚀 Registrando usuario en el backend...', data);

      const recaptchaToken = await getRecaptchaToken('register');
      const savedUser = await saveUser({
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        password: data.password,
        country: data.country,
        timezone: data.timezone,
        role: 'client',
        isVerified: true,
        isFirstLogin: true
      }, recaptchaToken);

      console.log('✅ Usuario registrado exitosamente:', savedUser);

      setBasicData(data);
      setSavedUserData(savedUser);
      setCurrentStep('complete');
    } catch (error: any) {
      console.error('❌ Error registrando usuario:', error);
      throw new Error(error.message || 'Error al registrar usuario. Por favor, inténtalo de nuevo.');
    }
  };

  useEffect(() => {
    if (currentStep !== 'complete' || !savedUserData || !onRegistrationComplete) return undefined;

    const timeout = window.setTimeout(() => {
      onRegistrationComplete(savedUserData);
    }, 4500);

    return () => window.clearTimeout(timeout);
  }, [currentStep, savedUserData, onRegistrationComplete]);

  const renderCompleteStep = () => {
    const data = basicData;

    const goHomeNow = () => {
      if (savedUserData && onRegistrationComplete) {
        onRegistrationComplete(savedUserData);
      }
    };

    return (
      <div className="min-h-screen bg-[linear-gradient(180deg,#f8fafc_0%,#eef2f6_100%)] px-4 py-10">
        <div className="mx-auto grid min-h-[calc(100vh-5rem)] w-full max-w-6xl items-center gap-6 lg:grid-cols-[minmax(0,1fr)_520px]">
          <section className="hidden overflow-hidden rounded-[2rem] bg-slate-950 text-white shadow-[0_34px_90px_-45px_rgba(15,23,42,0.45)] lg:block">
            <div className="relative h-72">
              <img src="/peekpassclubverde_headerlogo.png" alt={PEEKPASS_BRAND} className="absolute inset-0 h-full w-full scale-[1.25] object-cover object-center opacity-80" />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/30 to-transparent" />
            </div>
            <div className="p-8">
              <p className="text-[11px] font-black uppercase tracking-[0.28em] text-[#dff38c]">PEEKPASS.CLUB</p>
              <h2 className="mt-3 text-4xl font-black tracking-[-0.06em]">Tu cuenta quedó lista para descubrir eventos y gestionar entradas digitales.</h2>
              <p className="mt-4 text-sm leading-7 text-white/70">
                Desde el inicio vas a poder encontrar nuevas fechas, comprar tickets protegidos y, si sos organizador, comenzar a publicar tus propios eventos.
              </p>
            </div>
          </section>

          <section className="w-full rounded-[2rem] border border-slate-200 bg-white p-7 text-center shadow-[0_24px_70px_-38px_rgba(15,23,42,0.22)] sm:p-9">
            <img src="/PKSMILE_Logo.png" alt={PEEKPASS_BRAND} className="mx-auto h-20 w-20 object-contain" />
            <p className="mt-5 text-[11px] font-black uppercase tracking-[0.28em] text-[#536d12]">{PEEKPASS_BRAND}</p>
            <div className="mx-auto mt-5 flex h-16 w-16 items-center justify-center rounded-full bg-[#dff38c] text-slate-950">
              <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>

            <h1 className="mt-5 text-3xl font-black tracking-[-0.05em] text-slate-950">¡Bienvenido a PEEKPASS.CLUB!</h1>
            <p className="mx-auto mt-3 max-w-md text-sm leading-7 text-slate-600">
              Tu cuenta fue creada correctamente. Ya podés descubrir eventos, comprar entradas y, si sos organizador, comenzar a publicar tus propios eventos.
            </p>

            <div className="mt-7 grid gap-3 text-left sm:grid-cols-2">
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <p className="text-xs font-black uppercase tracking-[0.18em] text-slate-500">Cuenta</p>
                <p className="mt-2 text-sm font-black text-slate-950">{data?.firstName} {data?.lastName}</p>
                <p className="mt-1 break-all text-xs text-slate-500">{data?.email}</p>
              </div>
              <div className="rounded-2xl border border-[#d7ed85] bg-[#f7fce6] p-4">
                <p className="text-xs font-black uppercase tracking-[0.18em] text-[#536d12]">Estado</p>
                <p className="mt-2 text-sm font-black text-slate-950">Acceso activo</p>
                <p className="mt-1 text-xs leading-5 text-slate-600">Listo para operar en la ticketera.</p>
              </div>
            </div>

            <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-4 text-left">
              <p className="text-sm font-black text-slate-950">Próximo paso</p>
              <p className="mt-2 text-sm leading-6 text-slate-600">
                Te llevaremos al inicio para que explores la agenda de PEEKPASS.CLUB. Desde tu cuenta podrás consultar compras, entradas y novedades.
              </p>
            </div>

            <div className="mt-7 flex items-center justify-center gap-3 rounded-2xl bg-slate-50 px-4 py-3">
              <span className="h-5 w-5 animate-spin rounded-full border-2 border-slate-300 border-b-[#536d12]" />
              <p className="text-sm font-bold text-slate-700">Serás redirigido al inicio en unos segundos...</p>
            </div>

            <button
              type="button"
              onClick={goHomeNow}
              className="mt-6 w-full rounded-xl bg-slate-950 px-5 py-4 text-sm font-black text-white transition hover:bg-[#536d12]"
            >
              Ir al inicio ahora
            </button>
          </section>
        </div>
      </div>
    );
  };

  switch (currentStep) {
    case 'basic':
      return <RegistrationForm onComplete={handleBasicFormComplete} onGoToLogin={onGoToLogin} />;

    case 'complete':
      return renderCompleteStep();

    default:
      return <RegistrationForm onComplete={handleBasicFormComplete} onGoToLogin={onGoToLogin} />;
  }
};

export default RegistrationFlow;
